import React, { useEffect } from 'react';
import { Message } from '@iwoca/orion';
import { LayoutHeadings } from 'components/Layout/InnerLayout/LayoutHeadings/LayoutHeadings';
import { useAmplitude } from 'hooks/useAmplitude';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useOffers } from '../hooks/useOffers';
import { ChecksRequired } from './ChecksRequired/ChecksRequired';
import { ConfirmedOffer } from './ConfirmedOffer/ConfirmedOffer';
import { IndicativeOffer } from './IndicativeOffer/IndicativeOffer';
import { OffersError } from './OffersError/OffersError';
import { OffersLoader } from './OffersLoader/OffersLoader';
import styles from './ProductOffers.module.css';

export const ProductOffers = () => {
  const { trackEvent } = useAmplitude();

  const {
    isLoadingOffers,
    isRefetchingOffers,
    isLoadingState,
    offers,
    setSelectOfferError,
    selectOfferError,
    signupError,
    shouldRedirect,
    hasSignupDecision,
    isErrorChecked,
    setIsErrorChecked,
    isSecurityCheckRequired,
    handleRedirect,
    autoBankStatements,
  } = useOffers({
    getOffersEnabled: true,
  });

  const confirmedOffers = offers?.filter(
    (offer) => offer.offer_type === 'confirmed',
  );

  const indicativeOffers = offers?.filter(
    (offer) => offer.offer_type === 'indicative',
  );

  useEffect(() => {
    trackEvent(AMPLITUDE_EVENTS.newApplicationProductOffersPending);
    setIsErrorChecked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasDataAvailable =
      !isLoadingOffers &&
      !isRefetchingOffers &&
      isErrorChecked &&
      !signupError &&
      !isLoadingState &&
      !isSecurityCheckRequired;

    if (offers && hasDataAvailable) {
      handleRedirect(offers);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingOffers,
    isRefetchingOffers,
    offers,
    isErrorChecked,
    isLoadingState,
    isSecurityCheckRequired,
  ]);

  if (
    !offers ||
    isLoadingOffers ||
    isRefetchingOffers ||
    !hasSignupDecision ||
    shouldRedirect
  ) {
    return <OffersLoader />;
  }

  if (signupError) {
    return <OffersError error={signupError} />;
  }

  if (isSecurityCheckRequired) {
    return <ChecksRequired />;
  }

  return (
    <div className={styles.productOffers}>
      <LayoutHeadings
        heading="Good news - the application has passed our initial checks"
        headingVariant="long"
        subHeading={
          autoBankStatements
            ? 'We are not able to provide an instant offer for this particular application. However, we will be able to review this application within 24 hours following a submission of bank statements.'
            : 'To continue, select the preferred option below. If we need anything else to confirm your offer, we’ll let you know at the next step.'
        }
      />

      <div className={styles.offers}>
        {indicativeOffers && indicativeOffers?.length > 0 && (
          <IndicativeOffer offers={indicativeOffers} />
        )}
        {confirmedOffers && confirmedOffers?.length > 0 && (
          <>
            <p className={styles.confirmedOfferText}>
              Here are some other options you can choose from:
            </p>
            {confirmedOffers.map(
              ({ offer_id, max_amount, interest_rate, duration }) => (
                <ConfirmedOffer
                  key={`confirmed-${offer_id}`}
                  maxAmount={max_amount}
                  rate={interest_rate}
                  duration={duration}
                  offerId={offer_id}
                  setSelectOfferError={setSelectOfferError}
                />
              ),
            )}
          </>
        )}
      </div>
      {selectOfferError && (
        <Message
          message="Something went wrong, please try again."
          type="error"
        />
      )}
    </div>
  );
};
